import { useEffect, useState } from "react";
import { FileDown, FileSpreadsheet, Sheet, Send, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { addDays, weekStart, fmtStamp } from "@/lib/format";
import { PageHeader, Field, NativeSelect, Spinner } from "@/components/Shared";

export default function Exports() {
  const { meta } = useAuth();
  const today = meta.today;
  const [range, setRange] = useState({ start: today, end: today });
  const [photos, setPhotos] = useState(true);
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState("");
  const [projects, setProjects] = useState([]);
  const [wb, setWb] = useState({ project_id: "", start: "", end: "" });
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/exports/email-log").then((r) => setLog(r.data));
  }, []);
  useEffect(() => {
    if (range.start && range.end) api.get("/exports/summary", { params: range }).then((r) => setSum(r.data)).catch(() => setSum(null));
  }, [range]);

  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  const dl = (format) => run(format, () => downloadFile("/exports/daily", { ...range, format, photos: format === "pdf" ? photos : undefined }));
  const presets = [["Today", today, today], ["Yesterday", addDays(today, -1), addDays(today, -1)], ["This week", weekStart(today), today], ["Last 7 days", addDays(today, -6), today]];
  const sendNow = () => run("email", async () => {
    const { data } = await api.post("/exports/daily/email", { date: range.start });
    toast.success(`Daily summary emailed (${data.report_count} reports)`);
    setLog((await api.get("/exports/email-log")).data);
  });

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Documentation" title="Exports" />
      <div className="grid gap-5 lg:grid-cols-5">
        <section className="panel p-5 lg:col-span-3" data-testid="daily-export-card">
          <p className="font-display text-2xl font-semibold">Daily / period report</p>
          <p className="mt-1 text-sm text-slate-500">Compiles submitted reports (drafts excluded) organized by Client → Project → Inspector, each in the Inspectors Daily Progress Report layout.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {presets.map(([l, s, e]) => (
              <button key={l} onClick={() => setRange({ start: s, end: e })} data-testid={`export-preset-${l.toLowerCase().replace(/\s/g, "-")}`}
                className={`h-9 rounded-full border px-4 text-sm font-semibold transition-colors ${range.start === s && range.end === e ? "border-[#5C241E] bg-[#5C241E] text-white" : "border-border bg-white hover:border-[#5C241E]"}`}>{l}</button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Field label="From"><input type="date" className="field-input" value={range.start} onChange={(e) => setRange({ start: e.target.value, end: e.target.value > range.end ? e.target.value : range.end })} data-testid="export-start-input" /></Field>
            <Field label="To"><input type="date" className="field-input" value={range.end} min={range.start} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="export-end-input" /></Field>
          </div>
          {sum && (
            <div className="mt-4 rounded-md border border-border bg-slate-50 p-4" data-testid="export-summary">
              <p className="font-display text-lg font-semibold">{sum.title}</p>
              <div className="font-mono-num mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                <span><b data-testid="export-summary-count">{sum.report_count}</b> reports</span><span><b>{sum.employees.length}</b> employees</span>
                <span><b>{sum.total_hours}</b> hours</span><span><b>{sum.photo_count}</b> photos</span>
              </div>
              {sum.projects.length > 0 && <p className="mt-2 text-xs text-slate-500">Projects: {sum.projects.join(", ")}</p>}
            </div>
          )}
          <label className="mt-4 flex items-center gap-2 text-sm"><input type="checkbox" className="h-5 w-5 accent-[#5C241E]" checked={photos} onChange={(e) => setPhotos(e.target.checked)} data-testid="export-include-photos" />Include photo thumbnails in PDF</label>
          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            <button className="btn-brand h-12" disabled={!!busy} onClick={() => dl("pdf")} data-testid="export-pdf-button">{busy === "pdf" ? <Spinner className="text-white" /> : <FileDown className="h-4 w-4" />}PDF</button>
            <button className="btn-ghost h-12" disabled={!!busy} onClick={() => dl("xlsx")} data-testid="export-xlsx-button">{busy === "xlsx" ? <Spinner /> : <FileSpreadsheet className="h-4 w-4" />}Excel</button>
            <button className="btn-ghost h-12" disabled={!!busy} onClick={() => dl("csv")} data-testid="export-csv-button">{busy === "csv" ? <Spinner /> : <Sheet className="h-4 w-4" />}CSV</button>
          </div>
        </section>

        <div className="space-y-5 lg:col-span-2">
          <section className="panel p-5" data-testid="project-workbook-card">
            <p className="font-display text-2xl font-semibold">Project daily workbook</p>
            <p className="mt-1 text-sm text-slate-500">One Excel tab per day, just like your current job spreadsheets — previous footage links to the prior day's tab.</p>
            <div className="mt-4 space-y-3">
              <NativeSelect value={wb.project_id} onChange={(e) => setWb({ ...wb, project_id: e.target.value })} aria-label="Project" data-testid="workbook-project-select">
                <option value="">Select project…</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.client_name})</option>)}
              </NativeSelect>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" className="field-input" value={wb.start} onChange={(e) => setWb({ ...wb, start: e.target.value })} data-testid="workbook-start-input" aria-label="From" />
                <input type="date" className="field-input" value={wb.end} onChange={(e) => setWb({ ...wb, end: e.target.value })} data-testid="workbook-end-input" aria-label="To" />
              </div>
              <button className="btn-ghost h-12 w-full" disabled={!wb.project_id || !!busy} onClick={() => run("wb", () => downloadFile(`/exports/project/${wb.project_id}/workbook`, { start: wb.start || undefined, end: wb.end || undefined }))} data-testid="workbook-download-button">
                {busy === "wb" ? <Spinner /> : <FileSpreadsheet className="h-4 w-4" />}Download workbook
              </button>
            </div>
          </section>

          <section className="panel p-5" data-testid="email-summary-card">
            <p className="flex items-center gap-2 font-display text-2xl font-semibold"><Mail className="h-5 w-5 text-[#5C241E]" />Email daily summary</p>
            <p className="mt-1 text-sm text-slate-500">
              {settings?.daily_enabled ? `Automatic delivery is ON at ${settings.daily_time} (${settings.timezone}).` : "Automatic delivery is off — turn it on in Settings."}
              {" "}Recipients: {settings?.recipient_emails?.length ? settings.recipient_emails.join(", ") : "none yet"}.
            </p>
            <button className="btn-brand mt-4 h-12 w-full" disabled={!!busy || !settings?.recipient_emails?.length} onClick={sendNow} data-testid="email-daily-now-button">
              {busy === "email" ? <Spinner className="text-white" /> : <Send className="h-4 w-4" />}Email summary for {range.start}
            </button>
            {log.length > 0 && (
              <div className="mt-4 border-t border-border pt-3 text-xs text-slate-600" data-testid="email-log">
                {log.slice(0, 5).map((l) => <p key={l.sent_at}>{l.date} · {l.report_count} reports · sent {fmtStamp(l.sent_at)}</p>)}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
